import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import EditorFormik from '~/components/communications/EmailCommunicationCard/EmailEditor/EditorFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import { buildGetTemplateTokensQueryParams } from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/utils';
import GenericTemplateContentContainer from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateCard/GenericTemplateContentContainer';
import PencilIcon from '~/components/icons/PencilIcon';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { SuggestionToken } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import Divider from '~/components/ui/atoms/Divider';
import useDataFetcher from '~/components/useDataFetcher';

import TEXTS from './texts';

export interface LinkShareTemplateValue {
  template: string;
  isDefault?: boolean;
}

export type LinkShareTemplateSection = 'email' | 'sms';

export interface LinkShareTemplatesConfigurationProps {
  heading: string;
  subtitle: string;
  // Top to bottom; email first when unset.
  sectionOrder?: [LinkShareTemplateSection, LinkShareTemplateSection];
  // The token catalog's `feature_specific`: which feature's tokens the editors offer.
  featureSpecific: string;
  emailSectionTitle: string;
  smsSectionTitle: string;
  emailTitle: LinkShareTemplateValue;
  emailBody: LinkShareTemplateValue;
  sms: LinkShareTemplateValue;
  // Each handler reports its own failure and then rejects, which keeps the dialog open.
  onSaveEmail: (values: { title_template?: string; body_template: string }) => Promise<void>;
  onSaveSms: (values: { body_template: string }) => Promise<void>;
  // Offered only while the section holds a customized template: the email's once either part does.
  onResetEmail?: () => Promise<void>;
  onResetSms?: () => Promise<void>;
}

interface FormValues {
  title_template?: string | undefined;
  body_template: string;
}

interface TemplateSectionProps {
  onEdit: () => void;
  onReset?: () => void;
  titleTemplate?: string;
  bodyTemplate: string;
  title: string;
}

interface TemplateTokensResponse {
  tokens: SuggestionToken[];
}

const TemplateSection: React.FC<TemplateSectionProps> = ({ onEdit, onReset, titleTemplate, bodyTemplate, title }) => {
  return (
    <div className="my-20 flex flex-col items-center justify-between">
      <div className="flex w-full items-center justify-between">
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} weight={Text.WEIGHTS.SEMI_BOLD}>
          {title}
        </Text>
        <div className="flex items-center">
          {onReset ? (
            <Button variant="text" color="primary" onClick={onReset}>
              {TEXTS.linkShareTemplates.resetToDefault}
            </Button>
          ) : null}
          <Button variant="text" color="primary" onClick={onEdit} startIcon={<PencilIcon iconColor="currentColor" />}>
            {TEXTS.linkShareTemplates.edit}
          </Button>
        </div>
      </div>
      <Divider className="w-full" />
      <GenericTemplateContentContainer titleTemplate={titleTemplate} bodyTemplate={bodyTemplate} className="w-full" />
    </div>
  );
};

const TemplateEditorSkeleton = () => <SkeletonTable rowsCount={3} columnsCount={1} maxHeight="100px" />;

const DEFAULT_SECTION_ORDER: [LinkShareTemplateSection, LinkShareTemplateSection] = ['email', 'sms'];

// The email and SMS that share a link, each edited with the token editor: the video-call invitation's.
const LinkShareTemplatesConfiguration: React.FC<LinkShareTemplatesConfigurationProps> = ({
  heading,
  subtitle,
  sectionOrder = DEFAULT_SECTION_ORDER,
  featureSpecific,
  emailSectionTitle,
  smsSectionTitle,
  emailTitle,
  emailBody,
  sms,
  onSaveEmail,
  onSaveSms,
  onResetEmail,
  onResetSms,
}) => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();
  const [editTemplateType, setEditTemplateType] = React.useState<'Sms' | 'Email' | null>(null);
  const [isUpdatingConfiguration, setIsUpdatingConfiguration] = React.useState(false);
  const { data, isLoading, isError } = useDataFetcher<TemplateTokensResponse>(
    `/api/v1/generic_templates/organization/${organization.id}/template/tokens?${buildGetTemplateTokensQueryParams({
      lobs: [],
      is_claim_level: true,
      feature_specific: featureSpecific,
    })}`,
    {}
  );
  const onClose = () => {
    setEditTemplateType(null);
  };

  const handleReset = async (reset: () => Promise<void>) => {
    setIsUpdatingConfiguration(true);
    try {
      await reset();
    } catch {
      // The handler reports its own failure; the section comes back as it was.
    } finally {
      setIsUpdatingConfiguration(false);
    }
  };

  const isEmailDefault = emailTitle.isDefault && emailBody.isDefault;

  const suggestions =
    data?.tokens && !isLoading
      ? data.tokens.map((token: SuggestionToken) => ({
          label: token.display_name,
          id: token.token_key,
          url_safe: token.url_safe,
        }))
      : [];
  return (
    <>
      <div>
        <div className="flex items-center justify-between">
          <Heading variant={Heading.TYPES.H3}>{heading}</Heading>
        </div>
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.PRIMARY} className="opacity-50">
          {subtitle}
        </Text>
        {isUpdatingConfiguration ? (
          <SkeletonTable rowsCount={2} columnsCount={1} maxHeight="100px" />
        ) : (
          sectionOrder.map((section) =>
            section === 'email' ? (
              <TemplateSection
                key={section}
                onEdit={() => {
                  setEditTemplateType('Email');
                }}
                onReset={onResetEmail && !isEmailDefault ? () => handleReset(onResetEmail) : undefined}
                title={emailSectionTitle}
                titleTemplate={emailTitle.template}
                bodyTemplate={emailBody.template}
              />
            ) : (
              <TemplateSection
                key={section}
                onEdit={() => {
                  setEditTemplateType('Sms');
                }}
                onReset={onResetSms && !sms.isDefault ? () => handleReset(onResetSms) : undefined}
                title={smsSectionTitle}
                bodyTemplate={sms.template}
              />
            )
          )
        )}
      </div>
      {editTemplateType ? (
        <Formik<FormValues>
          initialValues={
            editTemplateType === 'Email'
              ? {
                  title_template: emailTitle.template,
                  body_template: emailBody.template,
                }
              : { body_template: sms.template }
          }
          validationSchema={Yup.object().shape({
            title_template: Yup.string().nullable(),
            body_template: Yup.string().required(TEXTS.linkShareTemplates.required),
          })}
          onSubmit={async (values) => {
            setIsUpdatingConfiguration(true);
            try {
              if (editTemplateType === 'Email') {
                await onSaveEmail({ title_template: values.title_template, body_template: values.body_template });
              }
              if (editTemplateType === 'Sms') {
                await onSaveSms({ body_template: values.body_template });
              }
              onClose();
            } catch {
              // The handler reports its own failure; the dialog stays open for another try.
            } finally {
              setIsUpdatingConfiguration(false);
            }
          }}
        >
          {({ handleSubmit, isSubmitting }) => (
            <CardDialog
              isDialog
              title={TEXTS.linkShareTemplates.editDialogTitle(editTemplateType)}
              onClose={onClose}
              preventClose={isSubmitting}
              fullWidth
              maxWidth="md"
            >
              {/* The token catalog is what tells the editors which `{{...}}` placeholders exist, and the
               *  link dialog only rejects an unsupported one when it has that catalog. Gate the editors on
               *  the request instead of rendering them with an empty list, which `useDataFetcher` also
               *  leaves behind for good on a failed request. */}
              <LoadingSwitch isLoading={isLoading} isError={isError} LoadingComponent={TemplateEditorSkeleton}>
                {editTemplateType === 'Email' ? (
                  <>
                    <EditorFormik
                      label={TEXTS.linkShareTemplates.emailSubjectLabel}
                      id="title_template"
                      autoFocus
                      displayConfig={{ suggestions: !!suggestions }}
                      suggestionOptions={suggestions}
                    />
                    <EditorFormik
                      label={TEXTS.linkShareTemplates.emailBodyLabel}
                      id="body_template"
                      classNameEditor="min-h-[150px] overflow-auto"
                      autoFocus
                      displayConfig={{
                        font: true,
                        textColor: true,
                        textStyle: true,
                        align: true,
                        link: true,
                        suggestions: !!suggestions,
                      }}
                      suggestionOptions={suggestions}
                    />
                  </>
                ) : (
                  <EditorFormik
                    label={TEXTS.linkShareTemplates.smsBodyLabel}
                    id="body_template"
                    classNameEditor="min-h-[150px] overflow-auto"
                    autoFocus
                    displayConfig={{
                      suggestions: !!suggestions,
                    }}
                    suggestionOptions={suggestions}
                  />
                )}
              </LoadingSwitch>
              {/* The footer stays outside the gate: an unresolved catalog must not leave the dialog
               *  without a way out. Only Save is withheld, as on the email-configuration dialog. */}
              <div className="mt-20 flex w-full justify-end">
                <CancelButton disabled={isSubmitting} onClick={onClose} />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || isLoading || isError}
                  onClick={() => handleSubmit()}
                >
                  {TEXTS.linkShareTemplates.save}
                </Button>
              </div>
            </CardDialog>
          )}
        </Formik>
      ) : null}
    </>
  );
};

export default LinkShareTemplatesConfiguration;
